import React, { useState } from 'react';
import { OverlayPanel } from '../ui/OverlayPanel';

interface DockServicesPanelProps {
  isOpen: boolean;
  onClose: () => void;
  shipType: 'raft' | 'sloop' | 'caravel' | 'brigantine' | 'galleon' | 'man_o_war';
  currentHeat: number;
  maxHeat: number;
  currentHullHp: number;
  maxHullHp: number;
  currentSailHp: number;
  maxSailHp: number;
  berries: number;
  onReduceHeat: () => void;
  onReplenishHealth: () => void;
  onUpgradeShip?: () => void;
  isUpgrading?: boolean;
}

const SPACING = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
};

const FONT = {
  family: '"Sometype Mono", monospace',
  sizeSmall: 11,
  sizeMedium: 12,
  sizeLarge: 13,
  weightNormal: 400,
  weightBold: 700,
};

const COLORS = {
  text: '#e6edf3',
  textSecondary: '#8b949e',
  bgPrimary: '#0d1117',
  bgSecondary: '#0f172a',
  border: '#30363d',
  accentGreen: '#22c55e',
  accentGreenDark: '#059669',
  accentBlue: '#1f6feb',
  accentOrange: '#f59e0b',
  accentRed: '#dc2626',
  accentTeal: '#2a9d8f',
};

const buttonBaseStyle = {
  width: '100%' as const,
  padding: `${SPACING.sm}px ${SPACING.md}px`,
  border: '1px solid rgba(255,255,255,0.08)',
  color: COLORS.text,
  fontSize: FONT.sizeMedium,
  fontFamily: FONT.family,
  cursor: 'pointer' as const,
  textAlign: 'center' as const,
  textTransform: 'uppercase' as const,
  borderRadius: 0,
  fontWeight: FONT.weightNormal,
  transition: 'opacity 0.2s',
};

export const DockServicesPanel: React.FC<DockServicesPanelProps> = ({
  isOpen,
  onClose,
  shipType,
  currentHeat,
  maxHeat,
  currentHullHp,
  maxHullHp,
  currentSailHp,
  maxSailHp,
  berries,
  onReduceHeat,
  onReplenishHealth,
  onUpgradeShip,
  isUpgrading = false,
}) => {
  const [isReducingHeat, setIsReducingHeat] = useState(false);
  const [isReplenishing, setIsReplenishing] = useState(false);

  const handleReduceHeat = () => {
    setIsReducingHeat(true);
    onReduceHeat();
    setTimeout(() => {
      setIsReducingHeat(false);
    }, 500);
  };

  const handleReplenishHealth = () => {
    setIsReplenishing(true);
    onReplenishHealth();
    setTimeout(() => {
      setIsReplenishing(false);
    }, 500);
  };

  const heatPercentage = (currentHeat / maxHeat) * 100;
  const hullPercentage = (currentHullHp / maxHullHp) * 100;
  const sailPercentage = (currentSailHp / maxSailHp) * 100;
  const canUpgrade = shipType === 'raft';

  // Calculate health replenishment cost
  const missingHullHp = maxHullHp - currentHullHp;
  const missingSailHp = maxSailHp - currentSailHp;
  const COST_PER_HP = 10;
  const healthReplenishCost = Math.floor((missingHullHp + missingSailHp) * COST_PER_HP);
  const canAffordHealth = berries >= healthReplenishCost;
  const needsHealth = missingHullHp > 0 || missingSailHp > 0;

  return (
    <OverlayPanel
      isOpen={isOpen}
      onClose={onClose}
      title="DOCK SERVICES"
      draggable
      storageKey="dockServicesPanelPosition"
      defaultPosition={{ x: 400, y: 200 }}
      clampToViewport
      closeOnEscape={true}
      closeOnBackdrop={true}
      zIndex={950}
      width={380}
    >
      <div style={{ padding: SPACING.md, display: 'flex', flexDirection: 'column', gap: SPACING.lg }}>
        {/* Heat Reduction Section */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING.md }}>
          <div style={{ color: COLORS.textSecondary, fontSize: FONT.sizeSmall, textTransform: 'uppercase', letterSpacing: 1, fontFamily: FONT.family }}>
            Heat Management
          </div>

          {/* Heat Bar */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING.sm }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: FONT.sizeMedium, color: COLORS.text }}>Current Heat</span>
              <span style={{ fontSize: FONT.sizeMedium, color: heatPercentage > 75 ? COLORS.accentRed : heatPercentage > 50 ? COLORS.accentOrange : COLORS.accentGreen, fontWeight: FONT.weightBold }}>
                {Math.round(currentHeat)} / {maxHeat}
              </span>
            </div>

            {/* Heat Bar Visual */}
            <div style={{
              width: '100%',
              height: 20,
              backgroundColor: COLORS.bgSecondary,
              border: `1px solid ${COLORS.border}`,
              position: 'relative',
              overflow: 'hidden',
            }}>
              <div style={{
                width: `${heatPercentage}%`,
                height: '100%',
                backgroundColor: heatPercentage > 75 ? COLORS.accentRed : heatPercentage > 50 ? COLORS.accentOrange : COLORS.accentGreen,
                transition: 'width 0.3s ease',
              }} />
            </div>
          </div>

          {/* Reduce Heat Button */}
          <button
            onClick={handleReduceHeat}
            disabled={isReducingHeat}
            style={{
              ...buttonBaseStyle,
              backgroundColor: COLORS.accentTeal,
              opacity: isReducingHeat ? 0.6 : 1,
            }}
          >
            {isReducingHeat ? '[COOLING DOWN...]' : '[REDUCE HEAT]'}
          </button>

          <div style={{ fontSize: FONT.sizeSmall, color: COLORS.textSecondary, fontStyle: 'italic' }}>
            Reduces heat accumulation while docked. Heat will continue to decay naturally.
          </div>
        </div>

        {/* Health Replenishment Section */}
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: SPACING.md,
          paddingTop: SPACING.md,
          borderTop: `1px solid ${COLORS.border}`,
        }}>
          <div style={{ color: COLORS.textSecondary, fontSize: FONT.sizeSmall, textTransform: 'uppercase', letterSpacing: 1, fontFamily: FONT.family }}>
            Health Replenishment
          </div>

          {/* Hull HP Bar */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING.sm }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: FONT.sizeMedium, color: COLORS.text }}>Hull HP</span>
              <span style={{ fontSize: FONT.sizeMedium, color: hullPercentage > 75 ? COLORS.accentGreen : hullPercentage > 50 ? COLORS.accentOrange : COLORS.accentRed, fontWeight: FONT.weightBold }}>
                {Math.round(currentHullHp)} / {maxHullHp}
              </span>
            </div>

            {/* Hull HP Bar Visual */}
            <div style={{
              width: '100%',
              height: 16,
              backgroundColor: COLORS.bgSecondary,
              border: `1px solid ${COLORS.border}`,
              position: 'relative',
              overflow: 'hidden',
            }}>
              <div style={{
                width: `${hullPercentage}%`,
                height: '100%',
                backgroundColor: hullPercentage > 75 ? COLORS.accentGreen : hullPercentage > 50 ? COLORS.accentOrange : COLORS.accentRed,
                transition: 'width 0.3s ease',
              }} />
            </div>
          </div>

          {/* Sail HP Bar */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING.sm }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: FONT.sizeMedium, color: COLORS.text }}>Sail HP</span>
              <span style={{ fontSize: FONT.sizeMedium, color: sailPercentage > 75 ? COLORS.accentGreen : sailPercentage > 50 ? COLORS.accentOrange : COLORS.accentRed, fontWeight: FONT.weightBold }}>
                {Math.round(currentSailHp)} / {maxSailHp}
              </span>
            </div>

            {/* Sail HP Bar Visual */}
            <div style={{
              width: '100%',
              height: 16,
              backgroundColor: COLORS.bgSecondary,
              border: `1px solid ${COLORS.border}`,
              position: 'relative',
              overflow: 'hidden',
            }}>
              <div style={{
                width: `${sailPercentage}%`,
                height: '100%',
                backgroundColor: sailPercentage > 75 ? COLORS.accentGreen : sailPercentage > 50 ? COLORS.accentOrange : COLORS.accentRed,
                transition: 'width 0.3s ease',
              }} />
            </div>
          </div>

          {/* Cost Display */}
          {needsHealth && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: FONT.sizeMedium, color: COLORS.text }}>Replenish Cost:</span>
              <span style={{ fontSize: FONT.sizeMedium, color: canAffordHealth ? COLORS.accentGreen : COLORS.accentRed, fontWeight: FONT.weightBold }}>
                ₱{healthReplenishCost.toLocaleString()}
              </span>
            </div>
          )}

          {/* Replenish Health Button */}
          <button
            onClick={handleReplenishHealth}
            disabled={isReplenishing || !needsHealth || !canAffordHealth}
            style={{
              ...buttonBaseStyle,
              backgroundColor: COLORS.accentGreen,
              opacity: (isReplenishing || !needsHealth || !canAffordHealth) ? 0.6 : 1,
              cursor: (isReplenishing || !needsHealth || !canAffordHealth) ? 'not-allowed' : 'pointer',
            }}
          >
            {isReplenishing ? '[REPLENISHING...]' : !needsHealth ? '[FULL HEALTH]' : !canAffordHealth ? '[NOT ENOUGH ₱]' : '[REPLENISH HEALTH]'}
          </button>

          <div style={{ fontSize: FONT.sizeSmall, color: COLORS.textSecondary, fontStyle: 'italic' }}>
            Restores hull and sail HP to maximum. Cost: ₱10 per HP point.
          </div>
        </div>

        {/* Ship Upgrade Section */}
        {canUpgrade && onUpgradeShip && (
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: SPACING.md,
            paddingTop: SPACING.md,
            borderTop: `1px solid ${COLORS.border}`,
          }}>
            <div style={{ color: COLORS.textSecondary, fontSize: FONT.sizeSmall, textTransform: 'uppercase', letterSpacing: 1, fontFamily: FONT.family }}>
              Ship Upgrades
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING.sm }}>
              <div style={{ fontSize: FONT.sizeMedium, color: COLORS.text }}>
                Current Ship: <span style={{ color: COLORS.accentBlue, fontWeight: FONT.weightBold }}>{shipType.toUpperCase()}</span>
              </div>
              <div style={{ fontSize: FONT.sizeSmall, color: COLORS.textSecondary }}>
                Upgrade to a Sloop for improved speed and cargo capacity.
              </div>
            </div>

            <button
              onClick={onUpgradeShip}
              disabled={isUpgrading}
              style={{
                ...buttonBaseStyle,
                backgroundColor: COLORS.accentGreenDark,
                opacity: isUpgrading ? 0.6 : 1,
              }}
            >
              {isUpgrading ? '[UPGRADING...]' : '[UPGRADE TO SLOOP]'}
            </button>
          </div>
        )}

        {!canUpgrade && (
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: SPACING.md,
            paddingTop: SPACING.md,
            borderTop: `1px solid ${COLORS.border}`,
          }}>
            <div style={{ color: COLORS.textSecondary, fontSize: FONT.sizeSmall, textTransform: 'uppercase', letterSpacing: 1, fontFamily: FONT.family }}>
              Ship Status
            </div>
            <div style={{ fontSize: FONT.sizeMedium, color: COLORS.text }}>
              Current Ship: <span style={{ color: COLORS.accentBlue, fontWeight: FONT.weightBold }}>{shipType.toUpperCase()}</span>
            </div>
            <div style={{ fontSize: FONT.sizeSmall, color: COLORS.textSecondary }}>
              Your ship is fully upgraded. No further upgrades available.
            </div>
          </div>
        )}
      </div>
    </OverlayPanel>
  );
};
